import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, PackageSearch, Truck, Mail, MapPin, ExternalLink } from "lucide-react";
import useNoIndex from "@/hooks/useNoIndex";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView, track } from "@/lib/analytics";
import { BOOKS_SUPPORT } from "@/data/books";
import { trackPhysicalOrder } from "@/lib/physicalOrders";

const ACCENT = "#F57F17";

const STATUS_LABELS = {
  PAYMENT_PENDING: { label: "Payment Pending", color: "#9AA1AB" },
  PAYMENT_FAILED: { label: "Payment Failed", color: "#B3261E" },
  PAID: { label: "Order Confirmed", color: ACCENT },
  PREPARING: { label: "Being Prepared", color: ACCENT },
  PACKED: { label: "Packed", color: ACCENT },
  SHIPPED: { label: "Shipped", color: "#004D40" },
  DELIVERED: { label: "Delivered", color: "#004D40" },
  CANCELLED: { label: "Cancelled", color: "#9AA1AB" },
  REFUND_PENDING: { label: "Refund Pending", color: "#B3261E" },
  REFUNDED: { label: "Refunded", color: "#9AA1AB" },
};

function money(paise) {
  return `₹${Math.round(paise / 100)}`;
}

/**
 * Self-service "Track My Order" page — a customer who has lost their
 * confirmation email/link can still check status by entering their
 * order reference + the email used to place the order (Command 04
 * follow-up). No token required, but the backend only reveals anything
 * when both fields match the same order.
 */
export default function ChinnuOrderTracking() {
  useNoIndex();
  useTrackPageView("chinnu_track_order_view");
  useSEO({
    title: "Track Your Order — LifeIQ Chinnu",
    description: "Check the status of your Chinnu Talking Adventures printed-book order.",
    canonicalPath: "/books/chinnu/track-order",
  });

  const [orderRef, setOrderRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // null | {found:false} | {found:true, ...}

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderRef.trim() || !email.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const data = await trackPhysicalOrder(orderRef.trim(), email.trim());
      setResult(data);
      track("chinnu_track_order_lookup", { found: !!data.found });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const statusMeta = result?.found ? STATUS_LABELS[result.status] || { label: result.status, color: "#5C6470" } : null;

  return (
    <main className="min-h-[80vh] bg-[#FBF7EE]">
      <section className="max-w-xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-white border border-[#E5E1D3] mx-auto flex items-center justify-center" style={{ color: ACCENT }}>
            <PackageSearch size={26} />
          </div>
          <div className="mt-5 text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: ACCENT }}>Chinnu Printed Books</div>
          <h1 className="mt-2 font-serif-d text-[28px] sm:text-[34px] text-[#0D1B2A] leading-[1.15]">Track Your Order</h1>
          <p className="mt-2 text-[14px] text-[#5C6470]">Enter your order ID and the email you used at checkout.</p>
        </div>

        <form data-testid="track-order-form" onSubmit={handleSubmit} className="mt-8 bg-white border border-[#E5E1D3] rounded-sm p-6 sm:p-8 space-y-4">
          <div>
            <label htmlFor="track-order-ref" className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#5C6470]">Order ID</label>
            <input
              id="track-order-ref"
              data-testid="track-order-ref-input"
              value={orderRef}
              onChange={(e) => setOrderRef(e.target.value)}
              placeholder="e.g. LQ-CH-A1B2C3D4"
              className="mt-1.5 w-full border border-[#D9D4C4] rounded-sm px-3.5 py-2.5 text-[14px] text-[#0D1B2A] focus:outline-none focus:ring-2 focus:ring-[#F57F17]/40"
              required
            />
          </div>
          <div>
            <label htmlFor="track-order-email" className="text-[12px] font-semibold uppercase tracking-[0.1em] text-[#5C6470]">Email Used At Checkout</label>
            <input
              id="track-order-email"
              type="email"
              data-testid="track-order-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1.5 w-full border border-[#D9D4C4] rounded-sm px-3.5 py-2.5 text-[14px] text-[#0D1B2A] focus:outline-none focus:ring-2 focus:ring-[#F57F17]/40"
              required
            />
          </div>

          {error && (
            <p data-testid="track-order-error" className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-sm px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="track-order-submit-btn"
            disabled={loading}
            style={{ backgroundColor: ACCENT }}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] font-semibold tracking-wide uppercase rounded-sm text-white hover:opacity-90 disabled:opacity-70 transition-opacity"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
            {loading ? "Looking up your order…" : "Track Order"}
          </button>
        </form>

        {result && !result.found && (
          <div data-testid="track-order-not-found" className="mt-6 bg-white border border-[#E5E1D3] rounded-sm p-6 text-center">
            <p className="text-[14px] text-[#0D1B2A]">We couldn't find a matching order. Please check your order ID and email, or contact support below.</p>
            <a href={`mailto:${BOOKS_SUPPORT.email}`} data-testid="track-order-support-email" className="mt-3 inline-flex items-center gap-2 text-[13px] underline" style={{ color: ACCENT }}>
              <Mail size={14} /> {BOOKS_SUPPORT.email}
            </a>
          </div>
        )}

        {result?.found && (
          <div data-testid="track-order-result" className="mt-6 bg-white border border-[#E5E1D3] rounded-sm p-6 sm:p-7">
            <div className="flex items-center justify-between">
              <span data-testid="track-order-status-badge" className="text-[12px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-sm" style={{ color: statusMeta.color, backgroundColor: `${statusMeta.color}1A` }}>
                {statusMeta.label}
              </span>
              <span className="font-mono text-[13px] text-[#5C6470]">{result.order_ref}</span>
            </div>

            <div className="mt-5 space-y-2 text-[14px] text-[#0D1B2A]">
              <div className="flex justify-between"><span className="text-[#5C6470]">Product</span><span className="text-right font-medium">{result.product_title}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6470]">Quantity</span><span>{result.quantity}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6470]">Amount</span><span className="font-semibold">{money(result.total_amount)}</span></div>
            </div>

            <div className="mt-4 pt-4 border-t border-[#E5E7EA] flex items-start gap-2 text-[14px] text-[#0D1B2A]">
              <MapPin size={15} className="mt-0.5 shrink-0" style={{ color: ACCENT }} />
              <span>Delivering to: {result.delivery.full_name}, {result.delivery.address_summary}</span>
            </div>

            {(result.fulfilment?.courier || result.fulfilment?.tracking_number) && (
              <div className="mt-4 pt-4 border-t border-[#E5E7EA]">
                <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.16em] font-semibold text-[#5C6470]">
                  <Truck size={14} style={{ color: ACCENT }} /> Shipping Details
                </div>
                <div className="mt-2 text-[14px] text-[#0D1B2A] space-y-1">
                  {result.fulfilment.courier && <p>Courier: {result.fulfilment.courier}</p>}
                  {result.fulfilment.tracking_number && <p>Tracking No.: {result.fulfilment.tracking_number}</p>}
                  {result.fulfilment.dispatch_date && <p>Dispatched: {result.fulfilment.dispatch_date}</p>}
                  {result.fulfilment.tracking_url && (
                    <a href={result.fulfilment.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="track-order-courier-link" className="inline-flex items-center gap-1.5 underline" style={{ color: ACCENT }}>
                      Track with courier <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {!result.fulfilment?.courier && !result.fulfilment?.tracking_number && result.status !== "DELIVERED" && (
              <p className="mt-4 pt-4 border-t border-[#E5E7EA] text-[13px] text-[#5C6470]">
                Dispatch and tracking details will appear here once your order ships.
              </p>
            )}
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/books/chinnu" data-testid="track-order-back-link" className="text-[12px] uppercase tracking-wide font-semibold text-[#5C6470] hover:text-[#0D1B2A] transition-colors">
            ← Back to Chinnu Talking Adventures
          </Link>
        </div>
      </section>
    </main>
  );
}
